import { Injectable } from 'injection-js';
import { Actions } from "shared/state";
import { Module, State, Http } from 'shared/core';
import { AjaxForm, Flash, FlexTable } from 'shared/lib';
import { DoubleClickProtectionElement, RoyalMailQrCode } from "shared/elements";
import * as _ from 'lodash';

@Injectable()
export class AmendmentsModule extends Module {
  constructor(state: State, http: Http) {
    super();

    this.state = state;
    this.http = http;
  }

  state: State;
  http: Http;
  $body = $('body');
  ajaxForm = new AjaxForm();
  allowSubmit = false;

  public init(): void {
    // NB: this selects .flex-table.resizable page-wide (see shared/lib/flex_table.ts), not just on
    // this page. It's safe today because AmendmentsModule only actually runs for the `returns` catch-all
    // route in apps/webshop.ts - Returns Confirm (`/returns/:id/confirm`) matches an earlier, more
    // specific route entry first (Router uses Array#find - first match wins) and never reaches this
    // module. Reordering those route entries would silently break that isolation.
    new FlexTable('sm');

    new RoyalMailQrCode(this.http, this.state);

    // If a return reason is selected, filter out actions that can't be taken for this reason
    this.$body.on('change',  'select.returnReason', (e) => {
      this.filterActions(e.currentTarget);
      this.toggleCustomerCommentInput(e.currentTarget);
      this.filterSizes(e.currentTarget);
    });

    // If a select or input is changed, validate the changed row and check if all rows are valid
    this.$body.on('change', 'select, input.deleteItem', (e) => {
      // Perform validation
      this.validateRow(e.target.closest('.returnItemRow'));
      this.validateAllRows();
    });

    // If the input is changed, validate the changed row and check if all rows are valid. This is debounced so the
    // form doesn't submit on every key press
    this.$body.on('input', 'input', _.debounce(async (e) => {
      // Gather information so row can be found again after the new HTML
      const itemRow = e.target.closest('.returnItemRow');
      const containerRow = itemRow.closest('.returnRow');
      const idx = itemRow.dataset.idx;
      const iiId = containerRow.dataset.id;

      // Perform validation
      await this.validateRow(e.target.closest('.returnItemRow'));
      this.validateAllRows();

      //Find the container, row and comment input
      const newContainer = this.$body.find(`.returnRow[data-id="${iiId}"]`);
      const newRow = newContainer.find(`.returnItemRow${idx === '0' ? '[data-idx!="0"]:last' : `[data-idx="${idx}"]`}`);
      const commentBox = newRow.find('.returnComment');

      // Filthy hacks to make carat go to where the user has just finished typing
      const value = commentBox.val();
      commentBox.val('').trigger('focus').val(value);
    }, 800))

    // If the add item button is clicked, show the spare row and perform validation
    this.$body.on('click', '.addItemBtn', (e) => {
      const $btn = $(e.currentTarget)

      if ($btn.hasClass('button-disabled')) return

      $btn.attr(
        'title',
        'Complete the new row before adding another'
      ).addClass('button-disabled').tooltip();

      const $row = $btn.closest('.returnRow').find('.spare');
      $row.removeClass('hidden');
      this.validateRow($row[0]);
      this.validateAllRows();
    });

    // If a select or input is changed, validate the changed row and check if all rows are valid
    this.$body.on('change', '.returnAction', (e) => {
      // Perform validation
      const $row = $(e.target).closest('.returnItemRow');
      const reasonSelect = $row.find('select.returnReason');
      const requiresComment = reasonSelect.find('option:selected').data('requires-comment');
      const hasComment = $row.find('.return-sub-orders-td-customer-comment').find('input').val() !== '';

      if (!requiresComment || hasComment) {
        this.showSizeSelect(e.target);
      }
    });

    // If the delete button is clicked, check the delete checkbox
    this.$body.on('click', '.deleteBtn', (e) => {
      $(e.currentTarget.parentElement).find('input').prop('checked', true).change();
    });

    this.$body.on('click', '.notifyBtn', (e) => this.requestEmail(e));

    // Trigger a change event on the reason select to filter the available actions for the currently selected reason
    $('select.returnReason').change();

    ($('.size-select select') as any).sbsizing({
      maxHeight: 320,
    });

    this.state.dispatch({ name: Actions.OPEN_STATIC_MODAL, payload: 'tosEmailModal' });

    // Allow form submission now that everything has been initialised
    this.allowSubmit = true;
  }

  /**
    * Validate a single row of the return form. This checks all the inputs and selects in the row are valid. This locks
    * all other rows for the item to prevent edits if it isn't valid and will only unlock them once the row is valid. If
    * it is valid, the form is submitted and the response is handled. The page is reloaded if the response indicates it,
    * otherwise the row is replaced with provided HTML string.
    * @param row - The row to validate
   */
  private async validateRow(row: HTMLElement) {
    let valid = true;
    const $row = $(row);

    // If the row is hidden, return true
    if ($row.hasClass('hidden')) {
      return valid;
    }

    // Add the active class to the row. This is used when disabling other rows if the row is not valid
    $row.addClass('active');

    // Find all the selects and inputs in the row and validate them
    const qtySelect = $row.find('select.returnQuantity');
    const reasonSelect = $row.find('select.returnReason');
    const actionSelect = $row.find('select.returnAction');
    const itemSelect = $row.find('select.shopItem');
    const deleteCheckbox = $row.find('input.deleteItem');

    valid = this.validateSelect(qtySelect, reasonSelect, () => true) && valid;
    valid = this.validateSelect(reasonSelect, actionSelect, () => true) && valid;
    valid = this.validateSelect(actionSelect, itemSelect, () => actionSelect.val() === 'swap') && valid;

    // Only validate the item select if the action is swap
    if (actionSelect.val() === 'swap') {
      valid = this.validateSelect(itemSelect, null, () => false) && valid;
    }

    let requiresComment = reasonSelect.find('option:selected').data('requires-comment');

    if (requiresComment == true) {
      valid = $row.find('.return-sub-orders-td-customer-comment').find('input').val() !== '' && valid;
    }

    if (valid || deleteCheckbox.is(':checked')) {
      // If the row is valid or the delete checkbox is checked
      // Remove the active class from the row and enable all other rows
      $row.removeClass('active');
      this.enableOtherRows(row);

      // If the form is not allowed to submit, return at this point
      if (!this.allowSubmit) {
        return;
      }

      // Submit the form
      const response = await new AjaxForm().submitForm($row.closest('form') as JQuery<HTMLFormElement>, true);

      if (!!response.success) {
        // If the response is successful
        if (!!response.redirect) {
          // Reload the page if the response indicates it
          location.href = response.redirect;
          return;
        }

        // Parse the HTML string into a DOM element
        const replacementRow = $.parseHTML(response.row)[0];

        // Disable form submission while we mess around with the replacement row
        this.allowSubmit = false;

        // Find all the reason selects in the replacement row and filter the actions
        for (const select of Array.from($(replacementRow).find('.returnReason'))) {
          this.filterActions(select);
        }

        // Replace the row with the replacement row and check all rows are valid and trigger a change event on the
        // reason selects to filter the actions and sizes
        $row.closest('.returnRow').replaceWith(replacementRow);
        $('select.returnReason').change();
        this.validateAllRows();

        // Re-enable form submission
        this.allowSubmit = true;
      } else {
        // If the response is not successful, display the error message
        Flash.genericAlert(response.error);
      }
    } else {
      // If the row is not valid, disable all other rows if form submission is allowed
      if (this.allowSubmit) {
        this.disableOtherRows(row);
      }
    }
  }

  /**
   * Checks if the select has a value and is thus valid. If it does and the showNext callback returns true, nextSelect
   * is shown. If it doesn't, nextSelect is hidden and its value is set to null. Regardless, the validation result is
   * then returned.
   * @param select
   * @param nextSelect
   * @param showNext
   * @private
   */
  private validateSelect(select: JQuery<HTMLElement>, nextSelect: JQuery<HTMLElement>,  showNext: () => boolean): boolean {
    // If the select has a value, set valid to true
    const valid = !!$(select).val();

    if (valid && showNext()) {
      // If valid and showNext returns true, show the next select
      nextSelect.removeClass('hidden');
    } else {
        // Otherwise, hide the next select and set its value to null
      nextSelect?.addClass('hidden')?.val(null);
    }

    // Return the validation result
    return valid;
  }

  /** If a customer comment is required, show the input, otherwise hide it
   * @param select - The reason select element
   * @private
   */
  private toggleCustomerCommentInput(select: HTMLElement) {
    let requiresComment = $(select).find('option:selected').data('requiresComment');

    if (requiresComment) {
      let commentLabel = $(select).find('option:selected').data('commentLabel');

      $(select).closest('.returnItemRow').find('.return-sub-orders-td-customer-comment').removeClass('hidden');
      $(select).closest('.returnItemRow').find('.commentLabel').text(commentLabel);
    } else {
      $(select).closest('.returnItemRow').find('.return-sub-orders-td-customer-comment').addClass('hidden');
      $(select).closest('.returnItemRow').find('.commentLabel').text('');
    }
  }

  /** If a return reason is selected, filter out actions that can't be taken for this reason
   * @param select - The reason select element
   * @private
   */
  private filterActions(select: HTMLElement) {
    // Get the actions from the selected option and split them into an array
    const actions = $(select).find('option:selected').data('actions')?.split(',') ?? [];
    // Find the action select for the row
    const actionSelect = $(select).closest('.returnItemRow').find('.returnAction');

    if (this.allowSubmit) {
      // If form submission is allowed, reset the action select
      actionSelect.val(null);
    }

    // For each option in the action select, if the option is in the actions array, remove the hidden class and enable,
    // otherwise add it and disable.
    for (const option of Array.from(actionSelect.find('option'))) {
      if (actions.includes($(option).val()) || !$(option).val()) {
        $(option).removeClass('hidden').attr('disabled', null);
      } else {
        $(option).addClass('hidden').attr('disabled', '');
      }
    }
  }


  /** If a return reason is selected, filter out sizes that are irrelevant for this reason
   * @param select - The reason select element
   * @private
   */
  private filterSizes(select: HTMLElement) {
    const reasonId = parseInt($(select).find('option:selected').val() as string);
    const sizesSelect = $(select).closest('.returnItemRow').find('select.shopItem');

    sizesSelect.find('option').each((_index, option) => {
      const optionReasons = $(option).data('reasons');

      if (typeof optionReasons !== typeof undefined) {
        if (optionReasons.includes(reasonId)) {
          $(option).removeClass('hidden');
        } else {
          $(option).addClass('hidden');
        }
      }
    });
  }

  private showSizeSelect(select: HTMLElement) {
    const sizesSelect = $(select).closest('.returnItemRow').find('select.shopItem');
    const visibleSizes = sizesSelect.find('option:not(.hidden)').length;
    const action = $(select).closest('.returnItemRow').find('.returnAction').val();

    if (action !== 'swap') {
      sizesSelect.addClass('hidden');
      $(select).closest('.returnItemRow').find('.sizeWarning').addClass('hidden');
    } else if (visibleSizes === 1) {
      sizesSelect.addClass('hidden');
      $(select).closest('.returnItemRow').find('.sizeWarning').removeClass('hidden');
    } else {
      sizesSelect.removeClass('hidden');
      $(select).closest('.returnItemRow').find('.sizeWarning').addClass('hidden');
    }
  }

  /**
   * Validate all rows on the page and enable or disable the next button based on the result
   * @private
   */
  private validateAllRows() {
    let allValid: boolean;

    // Find all the rows that are not hidden
    const rows = this.$body.find('.returnItemRow:not(.hidden)');

    if (rows.length === 0) {
      // If there are no rows, the user cannot move on to the next step
      allValid = false;
    } else {
      // If an active row is found, the user cannot move on to the next step
      allValid = !rows.hasClass('active');
    }

    if (allValid) {
      // If all rows are valid, enable the next button
      $('.nextBtn').removeClass('button-disabled').prop('disabled', false);
    } else {
      // If all rows are not valid, disable the next button
      $('.nextBtn').addClass('button-disabled').prop('disabled', true);
    }
  }

  /**
   * Disables all other rows for the item by finding all the rows in the form that are not the active row and disabling
   * all the selects and inputs in them.
   * @param row
   * @private
   */
  private disableOtherRows(row: HTMLElement) {
    for (const inactiveRow of Array.from($(row).closest('form').find('.returnItemRow:not(.active)'))) {
      $(inactiveRow).find('select, input').prop('disabled', true);
    }
  }

  /**
   * Enables all other rows for the item by finding all the rows in the form that are not the active row and enabling
   * all the selects and inputs in them.
   * @param row
   * @private
   */
  private enableOtherRows(row: HTMLElement) {
    for (const inactiveRow of Array.from($(row).closest('form').find('.returnItemRow:not(.active)'))) {
      $(inactiveRow).find('select, input').prop('disabled', false);
    }
  }

  private async requestEmail(e: JQuery.ClickEvent) {
    const $btn = $(e.currentTarget);

    if ($btn.hasClass('button-disabled')) {
      return;
    }

    $btn.toggleClass(['button-action', 'button-disabled', 'button-transparent']);
    DoubleClickProtectionElement.disableContainer($btn.closest('.tos-email-item-grid'));

    const response: any = await this.http.post('sub_school_products/request_email', {
      child_id: $btn.data('childId'),
      sub_school_item_id: $btn.data('itemId'),
    });

    DoubleClickProtectionElement.enableContainer($btn.closest('.tos-email-item-grid'));

    const responseDiv = $.parseHTML(`<div class='tos-email-response'>${response.message}</div>`);

    if (response.success) {
      $(responseDiv).addClass('success');
    } else {
      $(responseDiv).addClass('error');
    }

    $btn.parent().prev().before(responseDiv);

    setTimeout(() => {
      $(responseDiv).fadeOut(500, () => $(responseDiv).remove());
    }, 3000);
  }
}
